/**
 * 사용자별 시간표 저장/불러오기 유틸리티
 *
 * Firebase Firestore 연동 포인트:
 *   saveUserTimetable  → Firestore users/{uid}/timetable set() 으로 교체
 *   loadUserTimetable  → Firestore users/{uid}/timetable get() 으로 교체
 *   getNextClassFromTimetable → 순수 함수, Firebase 연동 후에도 그대로 사용 가능
 */
import { PERIOD_TIMES } from '../data/periodTimes'
import { LEGACY_TIMETABLE_SEMESTER_KEY, getActiveId } from '../services/userDataService'
import { reconcileCourseClassroom, reconcileTimetableClassrooms } from '../utils/timetableClassroom'

// ── localStorage 키 상수 ─────────────────────────────────────────────
// 학기 구분이 없던 시절의 구 키. migration.js가 마이그레이션 소스로 읽는 용도로만 남긴다 —
// 새 코드는 semester-suffixed 키(`${ANON_TT_KEY}:${semesterKey}`)만 쓴다.
export const ANON_TT_KEY     = 'su-guide-timetable-v3'
export const ANON_SAVES_KEY  = 'suguide_saved_timetables'
export const ANON_ACTIVE_KEY = 'suguide_active_timetable_id'

/** 로그인 사용자 시간표 localStorage 키 */
export function getUserTimetableKey(uid) {
  return uid ? `suguide_timetable_${uid}` : null
}

// ── 저장 ──────────────────────────────────────────────────────────────

/**
 * 로그인 사용자의 시간표를 저장합니다 (localStorage).
 * Firebase 연동 시 → Firestore users/{uid}/timetable set() 으로 교체
 */
export function saveUserTimetable(uid, courses) {
  if (!uid || !Array.isArray(courses)) return
  try {
    localStorage.setItem(getUserTimetableKey(uid), JSON.stringify({
      courses,
      savedAt: new Date().toISOString(),
      version: 1,
    }))
    // 호출 측(TimetableApp 저장 effect)에서 suguide-timetable-changed 이벤트 발행
  } catch {}
}

/**
 * 비로그인(게스트) canonical 현재 시간표를 저장합니다 — su-guide-timetable-v3:{semesterKey}에
 * payload를 기존 값과 merge하여 씁니다. 로그인 사용자의 userDataService.saveTimetable에 대응하는
 * 게스트 전용 단일 write 경로 — TimetableApp과 courseRegistrationPlanningService가 함께 쓴다.
 * 이 함수 하나만 사용하고, 호출부에서 localStorage를 직접 쓰지 않는다.
 * @param {string} semesterKey
 * @param {object} payload
 * @param {Storage} [storage] 기본값 localStorage — 테스트에서 in-memory storage를 주입할 수 있다.
 */
// 쓰기 실패(용량 초과·저장소 차단)는 false로 알린다. 화면이 저장 성공을 거짓으로 보고하지 않게 한다.
export function saveAnonTimetable(semesterKey, payload, storage = globalThis.localStorage) {
  if (!semesterKey || !storage) return false
  try {
    const key = `${ANON_TT_KEY}:${semesterKey}`
    const raw = storage.getItem(key)
    const prev = raw ? JSON.parse(raw) : {}
    storage.setItem(key, JSON.stringify(reconcileTimetableClassrooms({ ...prev, ...payload })))
    return true
  } catch {
    return false
  }
}

/**
 * 게스트 활성 저장본(activeId가 가리키는 항목)이 있으면 그 저장본의 courses도 함께 갱신합니다.
 * 로그인 사용자의 userDataService.saveTimetableEntry에 대응하는 게스트 전용 동기화 함수.
 * @param {string} semesterKey
 * @param {Array} courses
 * @param {{ activeId?: string, storage?: Storage }} [opts] activeId를 명시하지 않으면 storage의
 *   활성 포인터를 읽는다. storage 기본값은 localStorage — 테스트에서 in-memory storage를 주입할 수 있다.
 */
export function syncActiveAnonSavedTimetable(semesterKey, courses, { activeId, storage = globalThis.localStorage } = {}) {
  if (!semesterKey || !storage) return
  try {
    const suffix = `:${semesterKey}`
    const resolvedActiveId = activeId ?? storage.getItem(`${ANON_ACTIVE_KEY}${suffix}`)
    if (!resolvedActiveId) return
    const savesRaw = storage.getItem(`${ANON_SAVES_KEY}${suffix}`)
    const saves = savesRaw ? JSON.parse(savesRaw) : []
    if (!Array.isArray(saves) || saves.length === 0) return
    const idx = saves.findIndex(e => String(e.id) === String(resolvedActiveId))
    if (idx < 0) return
    const now = new Date().toISOString()
    const next = saves.map((e, i) => i === idx ? { ...e, courses: reconcileTimetableClassrooms({ courses }).courses, updatedAt: now } : e)
    storage.setItem(`${ANON_SAVES_KEY}${suffix}`, JSON.stringify(next))
  } catch {}
}

// ── 불러오기 ─────────────────────────────────────────────────────────

/**
 * 로그인 사용자의 시간표를 불러옵니다 (localStorage).
 * Firebase 연동 시 → Firestore get() 으로 교체
 * @param {string|null} uid
 * @returns {Array|null}
 */
export function loadUserTimetable(uid) {
  if (!uid) return null
  try {
    const raw = localStorage.getItem(getUserTimetableKey(uid))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const courses = parsed.courses
    return Array.isArray(courses) && courses.length > 0 ? courses : null
  } catch { return null }
}

// 명시적인 activeId가 실제 저장본과 매칭될 때만 그 저장본의 courses를 반환합니다.
// activeId가 없거나 목록의 어떤 항목과도 매칭되지 않으면 null — 저장본 목록에 존재한다는
// 이유만으로 임의의(예: 첫 번째) 저장본을 현재 시간표처럼 사용하지 않습니다.
function resolveActiveSavedCourses(saves, activeId) {
  if (!Array.isArray(saves) || saves.length === 0 || !activeId) return null
  const active = saves.find(e => e.id === activeId)
  const courses = active?.courses
  return Array.isArray(courses) && courses.length > 0 ? courses : null
}

/**
 * 익명(미로그인) 공용 시간표를 불러옵니다. semesterKey로 스코프된 키만 본다 —
 * LEGACY_TIMETABLE_SEMESTER_KEY로 명시적으로 조회할 때만 구 무-suffix 키를 읽는다.
 * 우선순위: canonical 현재 시간표 → activeId가 가리키는 저장본(명시적 매칭일 때만) → null.
 * @param {string} semesterKey
 * @returns {Array|null}
 */
export function loadAnonTimetable(semesterKey) {
  try {
    const suffix = semesterKey ? `:${semesterKey}` : ''
    const isLegacyBoundary = semesterKey === LEGACY_TIMETABLE_SEMESTER_KEY
    const readScoped = (base) => localStorage.getItem(`${base}${suffix}`)
      ?? (isLegacyBoundary ? localStorage.getItem(base) : null)

    const raw = readScoped(ANON_TT_KEY)
    const data = raw ? JSON.parse(raw) : {}
    const canonical = data.courses ?? data.saved ?? data.selectedCourses
    if (Array.isArray(canonical)) {
      return canonical.map(course => reconcileCourseClassroom(course))
    }

    const savesRaw = readScoped(ANON_SAVES_KEY)
    const saves = savesRaw ? JSON.parse(savesRaw) : []
    const activeId = readScoped(ANON_ACTIVE_KEY)
    const activeCourses = resolveActiveSavedCourses(saves, activeId)
    return activeCourses ? activeCourses.map(course => reconcileCourseClassroom(course)) : null
  } catch { return null }
}

/**
 * 로그인 사용자의 canonical 캐시(suguide:tt:{uid}:{semesterKey})를 불러옵니다.
 * userDataService.CACHE.timetable(uid, semesterKey) 와 동일한 키 — Firestore 동기화 캐시.
 * @param {string|null} uid
 * @param {string} semesterKey
 * @returns {Array|null}
 */
function loadCanonicalTimetable(uid, semesterKey) {
  if (!uid) return null
  try {
    const raw = localStorage.getItem(`suguide:tt:${uid}:${semesterKey}`)
    if (!raw) return null
    const data = JSON.parse(raw)
    const courses = data?.courses
    return Array.isArray(courses)
      ? courses.map(course => reconcileCourseClassroom(course))
      : null
  } catch { return null }
}

/**
 * 로그인 사용자의 활성 저장본(suguide:saves:{uid}:{semesterKey} 중 activeId가 명시적으로
 * 가리키는 항목)을 불러옵니다. loadCanonicalTimetable이 비어 있을 때만 쓰는 폴백이며,
 * activeId가 없거나 매칭되는 저장본이 없으면 null을 반환한다(임의의 저장본을 쓰지 않는다).
 * @param {string} uid
 * @param {string} semesterKey
 * @returns {Array|null}
 */
function loadActiveSavedTimetable(uid, semesterKey) {
  try {
    const raw = localStorage.getItem(`suguide:saves:${uid}:${semesterKey}`)
    const saves = raw ? JSON.parse(raw) : []
    const activeId = getActiveId(uid, semesterKey)
    const courses = resolveActiveSavedCourses(saves, activeId)
    return courses ? courses.map(course => reconcileCourseClassroom(course)) : null
  } catch { return null }
}

/**
 * "현재 적용된 시간표" courses를 반환하는 단일 공용 read API입니다.
 * 시간표 탭·홈·Sugi·셔틀·학점관리 등 모든 화면이 이 함수 하나만 호출해야 하며,
 * localStorage key를 직접 해석하거나 우선순위 로직을 다시 구현하지 않습니다.
 *
 * 우선순위: canonical 현재 시간표(courses) → activeId가 실제 저장본과 매칭될 때만 그 저장본 → null.
 * 저장본 목록은 현재 시간표와 별개의 데이터이며, canonical이 존재하면 항상 그것을 우선한다 —
 * 저장본 목록에 존재한다는 이유만으로(activeId 없음/불일치 포함) 어떤 저장본도 현재 시간표로
 * 자동 승격되지 않는다.
 *
 * 라이브 학기(semesterKey)는 canonical/활성 저장본 키만 본다 — 구버전 user 키/익명 키는
 * 학기 구분이 없는 legacy 데이터라서 절대 fallback하지 않는다. LEGACY_TIMETABLE_SEMESTER_KEY로
 * 명시적으로 조회할 때만 그 구 키들을 읽는다.
 * @param {string|null} uid
 * @param {string} semesterKey
 * @returns {Array|null}
 */
export function loadEffectiveTimetable(uid, semesterKey) {
  if (uid) {
    const canonical = loadCanonicalTimetable(uid, semesterKey)
    if (canonical !== null) return canonical
    const activeSaved = loadActiveSavedTimetable(uid, semesterKey)
    if (activeSaved !== null) return activeSaved
    if (semesterKey !== LEGACY_TIMETABLE_SEMESTER_KEY) return null
    const userTt = loadUserTimetable(uid)
    if (userTt) return userTt
    return loadAnonTimetable(semesterKey)
  }
  return loadAnonTimetable(semesterKey)
}

/** Current timetable's stored term. Calendar year is not present in timetable data. */
export function getEffectiveTimetableTerm(uid, semesterKey) {
  try {
    if (uid) {
      const canonicalRaw = localStorage.getItem(`suguide:tt:${uid}:${semesterKey}`)
      if (canonicalRaw) {
        const term = Number(JSON.parse(canonicalRaw)?.semester)
        if (term === 1 || term === 2) return term
      }
      if (semesterKey === LEGACY_TIMETABLE_SEMESTER_KEY) {
        const legacyRaw = localStorage.getItem(getUserTimetableKey(uid))
        if (legacyRaw) {
          const term = Number(JSON.parse(legacyRaw)?.semester)
          if (term === 1 || term === 2) return term
        }
      }
    }
    if (uid && semesterKey !== LEGACY_TIMETABLE_SEMESTER_KEY) return null
    const suffix = semesterKey ? `:${semesterKey}` : ''
    const raw = localStorage.getItem(`${ANON_TT_KEY}${suffix}`)
      ?? (semesterKey === LEGACY_TIMETABLE_SEMESTER_KEY ? localStorage.getItem(ANON_TT_KEY) : null)
    const term = Number(raw ? JSON.parse(raw)?.semester : null)
    return term === 1 || term === 2 ? term : null
  } catch { return null }
}

// ── 다음 수업 계산 ────────────────────────────────────────────────────

const DAY_KR = { 0: '일', 1: '월', 2: '화', 3: '수', 4: '목', 5: '금', 6: '토' }

function periodStartMin(period) {
  const slot = PERIOD_TIMES[period]
  if (!slot?.start) return null
  const [h, m] = slot.start.split(':').map(Number)
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null
}

function periodEndMin(period) {
  const slot = PERIOD_TIMES[period]
  if (!slot?.end) return null
  const [h, m] = slot.end.split(':').map(Number)
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null
}

/**
 * 시간표에서 오늘의 다음 수업을 반환합니다.
 * 순수 함수 — Firebase 연동 후에도 그대로 사용 가능
 *
 * @param {Array}  courses  시간표 과목 배열
 * @param {Date}   date
 * @returns {{ name, room, startPeriod, endPeriod, remainingMin, startMin, endMin, ...} | null}
 */
export function getNextClassFromTimetable(courses, date = new Date()) {
  if (!Array.isArray(courses) || courses.length === 0) return null
  const todayStr = DAY_KR[date.getDay()]
  const nowMin   = date.getHours() * 60 + date.getMinutes()

  const next = courses
    .filter(c => {
      if (c.isOnline) return false
      const days = Array.isArray(c.days) ? c.days : (c.day ? [c.day] : [])
      if (!days.includes(todayStr)) return false
      const start = periodStartMin(c.startPeriod)
      return start != null && start > nowMin
    })
    .sort((a, b) =>
      (periodStartMin(a.startPeriod) ?? 0) - (periodStartMin(b.startPeriod) ?? 0)
    )[0] ?? null

  if (!next) return null
  const startMin = periodStartMin(next.startPeriod)
  const endMin   = periodEndMin(next.endPeriod ?? next.startPeriod)
  return {
    ...next,
    name: next.name ?? next.courseName ?? '',
    startMin,
    endMin,
    remainingMin: Math.max(0, startMin - nowMin),
  }
}
